import { LensTheme } from '@diligentcorp/atlas-theme-mui-lens';
import {
  Avatar,
  Box,
  CircularProgress,
  IconButton,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  DragDropContext,
  Draggable,
  Droppable,
  DropResult,
} from '@hello-pangea/dnd';
import { handleSuccessToast } from 'src/components/molecules/Toast';
import { GetCategoryConfigCategoriesQuery } from 'src/graphql/generated';
import { getInitials } from 'src/utils/getInitials';
import { DateFormats, formatDate } from 'src/utils/time';
import { CategoryTypeBadge } from '../atoms/CategoryTypeBadge';
import { useCategoryTableData } from '../hooks/useCategoryTableData';
import { CategoryEmptyState } from '../molecules/CategoryEmptyState';
import { CategoryTablePagination } from '../molecules/CategoryTablePagination';
import { CategoryTableToolbar } from '../molecules/CategoryTableToolbar';
import { CategoryDetailsDrawer } from './CategoryDetailsDrawer';
import { EditSpeakUpCategoryDrawer } from './EditSpeakUpCategoryDrawer';

type Category = NonNullable<
  GetCategoryConfigCategoriesQuery['getOrganisationCategories']
>[number];

const DEFAULT_LOCALE = 'en-GB';

interface SpeakUpCategoryTableProps {
  onAddCategory?: () => void;
  forceStatusFilter?: string;
  onForceStatusFilterConsumed?: () => void;
}

export const SpeakUpCategoryTable: React.FC<SpeakUpCategoryTableProps> = ({
  onAddCategory,
  forceStatusFilter,
  onForceStatusFilterConsumed,
}) => {
  const { tokens, presets } = useTheme<LensTheme>();
  const { getAvatarProps } = presets.AvatarPresets;
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(
    null,
  );
  const [editCategoryId, setEditCategoryId] = useState<number | null>(null);

  const {
    rows,
    allCategories,
    totalCount,
    isEmpty,
    page,
    rowsPerPage,
    statusFilter,
    isLoading,
    setPage,
    setRowsPerPage,
    setSearchTerm,
    setStatusFilter,
    setStatusFilterDirect,
    handleReorder,
  } = useCategoryTableData();

  const editCategory =
    editCategoryId !== null
      ? (allCategories.find((c) => c.id === editCategoryId) ?? null)
      : null;

  useEffect(() => {
    if (forceStatusFilter) {
      setStatusFilterDirect(
        forceStatusFilter as 'Active' | 'Form required' | 'Inactive',
      );
      onForceStatusFilterConsumed?.();
    }
  }, [forceStatusFilter, setStatusFilterDirect, onForceStatusFilterConsumed]);

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    const sourceIndex = (page - 1) * rowsPerPage + result.source.index;
    const destIndex = (page - 1) * rowsPerPage + result.destination.index;
    handleReorder(sourceIndex, destIndex);
  };

  if (isLoading) {
    return (
      <Box
        sx={{ display: 'flex', justifyContent: 'center', padding: '48px' }}
        data-testid="category-table-loading"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (isEmpty) {
    return <CategoryEmptyState onAddCategory={onAddCategory} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.core.spacing[3].value,
      }}
      data-testid="speak-up-category-table"
    >
      <CategoryTableToolbar
        onSearch={setSearchTerm}
        statusFilter={statusFilter}
        onStatusChange={setStatusFilter}
      />

      <TableContainer sx={{ position: 'relative', overflowX: 'auto' }}>
        <DragDropContext onDragEnd={onDragEnd}>
          <Table sx={{ tableLayout: 'auto', width: '100%' }}>
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: tokens.semantic.color.surface.variant.value,
                }}
              >
                <TableCell sx={{ width: '80px', minWidth: '80px' }}>
                  Order
                </TableCell>
                <TableCell sx={{ minWidth: '250px' }}>Category name</TableCell>
                <TableCell sx={{ minWidth: '200px' }}>Type</TableCell>
                <TableCell sx={{ minWidth: '150px' }}>Created by</TableCell>
                <TableCell sx={{ minWidth: '180px' }}>Created at</TableCell>
                <TableCell sx={{ minWidth: '180px' }}>Last updated</TableCell>
                <TableCell
                  sx={{
                    width: '73px',
                    position: 'sticky',
                    right: 0,
                    backgroundColor:
                      tokens.semantic.color.surface.variant.value,
                    textAlign: 'right',
                    boxShadow:
                      '-8px 0 16px -4px rgba(0, 0, 0, 0.2), -2px 0 4px 0 rgba(0, 0, 0, 0.15)',
                    zIndex: 2,
                    borderRadius: 0,
                  }}
                >
                  Action
                </TableCell>
              </TableRow>
            </TableHead>
            <Droppable droppableId="category-table">
              {(provided) => (
                <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ padding: '32px' }}
                      >
                        <Typography
                          sx={{
                            color: tokens.semantic.color.type.muted.value,
                          }}
                        >
                          No categories found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((category, index) => (
                      <Draggable
                        key={category.id}
                        draggableId={String(category.id)}
                        index={index}
                      >
                        {(dragProvided, snapshot) => (
                          <TableRow
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            data-testid={`category-row-${category.id}`}
                            onClick={() => setSelectedCategory(category)}
                            hover
                            sx={{
                              cursor: 'pointer',
                              ...(snapshot.isDragging && {
                                backgroundColor:
                                  tokens.semantic.color.surface.variant.value,
                                boxShadow: 2,
                                display: 'table',
                                tableLayout: 'fixed',
                                width: '100%',
                              }),
                            }}
                          >
                            <TableCell sx={{ width: '80px', minWidth: '80px' }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                }}
                              >
                                <span
                                  {...dragProvided.dragHandleProps}
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ display: 'flex', cursor: 'grab' }}
                                  data-testid={`category-drag-handle-${category.id}`}
                                >
                                  <i
                                    className="uil uil-draggabledots"
                                    style={{
                                      color:
                                        tokens.semantic.color.type.muted.value,
                                    }}
                                  />
                                </span>
                                <span>{category.order + 1}.</span>
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: '250px' }}>
                              <Tooltip
                                title={
                                  category.locales?.[DEFAULT_LOCALE]?.name ??
                                  'Untitled'
                                }
                              >
                                <Link
                                  component="button"
                                  type="button"
                                  underline="always"
                                  sx={{
                                    color:
                                      tokens.semantic.color.type.default.value,
                                    textAlign: 'left',
                                    fontSize: 'inherit',
                                    fontWeight:
                                      tokens.semantic.fontWeight.emphasis.value,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    maxWidth: '280px',
                                    display: 'inline-block',
                                  }}
                                  data-testid={`category-name-${category.id}`}
                                  onClick={() => setSelectedCategory(category)}
                                >
                                  {category.locales?.[DEFAULT_LOCALE]?.name ??
                                    'Untitled'}
                                </Link>
                              </Tooltip>
                            </TableCell>
                            <TableCell sx={{ minWidth: '200px' }}>
                              <Box sx={{ display: 'flex', gap: '4px' }}>
                                {category.isDeterminedCategory && (
                                  <CategoryTypeBadge type="determined" />
                                )}
                                {category.isReportedCategory && (
                                  <CategoryTypeBadge type="reported" />
                                )}
                              </Box>
                            </TableCell>
                            <TableCell sx={{ minWidth: '150px' }}>
                              {(() => {
                                const name =
                                  category.Author?.displayName ??
                                  category.Author?.name;
                                if (!name) return <>—</>;
                                return (
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '8px',
                                    }}
                                  >
                                    <Avatar
                                      {...getAvatarProps({
                                        size: 'medium',
                                        uniqueId: name,
                                      })}
                                      aria-label={name}
                                    >
                                      {getInitials(name)}
                                    </Avatar>
                                    {name}
                                  </Box>
                                );
                              })()}
                            </TableCell>
                            <TableCell sx={{ minWidth: '180px' }}>
                              {formatDate(
                                category.createdAt,
                                DateFormats.YYYY_MM_DD_TIME_SHORT,
                                false,
                              )}
                            </TableCell>
                            <TableCell sx={{ minWidth: '180px' }}>
                              {formatDate(
                                category.updatedAt,
                                DateFormats.YYYY_MM_DD_TIME_SHORT,
                                false,
                              )}
                            </TableCell>
                            <TableCell
                              sx={{
                                width: '73px',
                                position: 'sticky',
                                right: 0,
                                backgroundColor: snapshot.isDragging
                                  ? tokens.semantic.color.surface.variant.value
                                  : tokens.semantic.color.surface.default.value,
                                textAlign: 'center',
                                boxShadow:
                                  '-8px 0 16px -4px rgba(0, 0, 0, 0.2), -2px 0 4px 0 rgba(0, 0, 0, 0.15)',
                                zIndex: 2,
                              }}
                            >
                              <IconButton
                                size="small"
                                data-testid={`category-edit-${category.id}`}
                                aria-label="Edit category"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setEditCategoryId(category.id);
                                }}
                              >
                                <i className="uil uil-pen" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </TableBody>
              )}
            </Droppable>
          </Table>
        </DragDropContext>
      </TableContainer>

      <CategoryTablePagination
        totalCount={totalCount}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={setPage}
        onRowsPerPageChange={setRowsPerPage}
      />

      <CategoryDetailsDrawer
        open={!!selectedCategory}
        category={selectedCategory}
        onClose={() => setSelectedCategory(null)}
      />

      {editCategory && (
        <EditSpeakUpCategoryDrawer
          category={editCategory}
          isOpen
          onClose={() => setEditCategoryId(null)}
          onSuccess={() =>
            handleSuccessToast({
              toastMessage: 'Saving has been successful',
            })
          }
        />
      )}
    </Box>
  );
};
